import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  makeStyles,
  Button,
  Grid,
  Hidden,
  IconButton,
  Spinner,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@paintscout/ui'
import {
  EstimatorAvatar,
  EstimatorSelect,
  TagSelection,
  KeypadInputField,
  DropdownSelect,
  useClientOptions,
  useUsers
} from '@paintscout/ui-business'
import type { DropdownSelectOption } from '@paintscout/ui-business'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { useDragLayer } from 'react-dnd'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import CloseIcon from '@material-ui/icons/Close'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import FilterListIcon from '@material-ui/icons/FilterList'
import { getObjectLabels } from '@paintscout/util/builder/util/get-object-labels'
import { getQuoteTypeOptions, getPipelines, getQuoteTags } from '@paintscout/util/builder/options'
import { addAuth0Prefix, removeAuth0Prefix } from '@paintscout/util'
import { getCrews } from '@paintscout/util/builder'
import type { CalendarEvent, QuoteTag } from 'paintscout'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { useLocalStorage } from 'shared/hooks'
import { useProductionCalendar } from './ProductionCalendarContext'
import UnscheduledJobCard from './UnscheduledJobCard'
import { getProjectTypeLabel } from './util'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {},
    unscheduledJobsContainer: {
      // backgroundColor: theme.paintscout.palette.brand.Neutral.P85,
      backgroundColor: '#ffffff',
      borderTopLeftRadius: theme.shape.borderRadius,
      borderTopRightRadius: theme.shape.borderRadius,
      borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      // border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      padding: theme.spacing(0, 2, 2, 2),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(0, 1.5, 0.5, 1.5)
      }
    },
    unscheduledJobsContainerHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(1),
      minWidth: 0,
      cursor: 'pointer',
      [theme.breakpoints.down('xs')]: {
        // flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        // Bleed past the container's horizontal padding so the divider stretches edge-to-edge,
        // then re-add the same padding inline so the header content stays put.
        marginLeft: theme.spacing(-1.5),
        marginRight: theme.spacing(-1.5),
        marginBottom: theme.spacing(1),
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(2),
        borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
      }
    },
    unscheduledJobsContainerInner: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        gap: theme.spacing(1)
      }
    },
    collapseButton: {
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(0.5)
      }
    },
    unscheduledJobsTextContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      whiteSpace: 'nowrap'
    },
    unscheduledTitle: {
      color: `${theme.paintscout.palette.brand.Neutral.P40} !important`,
      fontWeight: 500,
      fontSize: theme.typography.pxToRem(16)
    },
    countChip: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 24,
      height: 22,
      padding: theme.spacing(0, 0.75),
      border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
      borderRadius: theme.shape.borderRadius,
      fontSize: '0.8125rem',
      fontWeight: 500,
      color: theme.paintscout.palette.brand.Neutral.P60,
      backgroundColor: '#ffffff'
    },
    infoIcon: {
      width: 16,
      height: 16,
      color: theme.paintscout.palette.brand.Neutral.P60
    },
    headerIconButton: {
      padding: theme.spacing(0.5),
      color: 'rgb(100, 116, 139)'
    },
    headerFilterCount: {
      position: 'absolute',
      top: -2,
      right: -2,
      border: `1px solid ${theme.palette.background.paper}`,
      backgroundColor: theme.palette.primary.main,
      borderRadius: '9999px',
      height: theme.spacing(2),
      width: theme.spacing(2),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
      color: theme.palette.background.paper,
      fontSize: '0.625rem',
      fontWeight: 600
    },
    headerFilterButtonWrapper: {
      position: 'relative',
      display: 'inline-flex'
    },
    unscheduledFilterButtonContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: theme.spacing(0.5)
    },
    headerDivider: {
      width: '1px',
      margin: theme.spacing(0, 0.5),
      height: theme.spacing(2.5),
      backgroundColor: theme.paintscout.palette.brand.Neutral.P90
    },
    headerClearButtonOutlined: {
      borderRadius: theme.borderRadius.md,
      border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85} !important`,
      padding: theme.spacing(0.25, 1.5),
      color: 'rgb(51, 65, 85)',
      backgroundColor: '#fff',
      '&:hover': {
        backgroundColor: `${theme.paintscout.palette.brand.Neutral.P90} !important`,
        border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85} !important`
      }
    },
    unscheduledFilterChipsRow: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing(0.75),
      justifyContent: 'flex-end',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      // Bleed past the container's horizontal padding so the borders span full width.
      marginLeft: theme.spacing(-2),
      marginRight: theme.spacing(-2),
      marginBottom: theme.spacing(1),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      [theme.breakpoints.down('xs')]: {
        marginLeft: theme.spacing(-1.5),
        marginRight: theme.spacing(-1.5),
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(1.5)
      }
    },
    unscheduledFilterChip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      padding: theme.spacing(0.5, 1.25, 0.5, 0.75),
      borderRadius: 999,
      backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
      color: 'rgb(51, 65, 85)',
      fontSize: '0.8125rem',
      lineHeight: 1
    },
    unscheduledFilterChipRemove: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 16,
      height: 16,
      padding: 0,
      border: 'none',
      background: 'transparent',
      color: 'rgb(100, 116, 139)',
      cursor: 'pointer',
      '&:hover': {
        color: 'rgb(51, 65, 85)'
      }
    },
    unscheduledFilterChipRemoveIcon: {
      width: 14,
      height: 14
    },
    unscheduledCloseIcon: {
      width: theme.spacing(2),
      height: theme.spacing(2)
    },
    filterButton: {
      height: theme.spacing(4.5),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5, 3)
    },
    mobileFilterButton: {
      position: 'relative',
      paddingRight: 0,
      paddingLeft: 0
    },
    filterButtonSecondary: {
      color: theme.paintscout.palette.brand.Neutral.P30,
      backgroundColor: theme.paintscout.palette.brand.Neutral.P90,
      '&&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P90
      },
      '&&:active': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P90
      },
      '&&:focus': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P80
      }
    },
    filterButtonPrimary: {
      color: theme.palette.background.paper,
      backgroundColor: theme.palette.primary.main,
      '&&:hover': {
        backgroundColor: theme.palette.primary.dark
      },
      '&&:active': {
        backgroundColor: theme.palette.primary.main
      },
      '&&:focus': {
        backgroundColor: theme.palette.primary.main
      }
    },
    mobileFilterCount: {
      position: 'absolute',
      top: -5,
      right: -5,
      border: `1px solid ${theme.palette.background.paper}`,
      backgroundColor: theme.palette.primary.main,
      borderRadius: '9999px',
      height: theme.spacing(2.5),
      width: theme.spacing(2.5),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
      color: theme.palette.background.paper
    },
    filterCount: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.primary.main,
      borderRadius: '9999px',
      width: theme.spacing(2.5),
      height: theme.spacing(2.5),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.75rem',
      marginTop: theme.spacing(0.25),
      marginLeft: theme.spacing(1)
    },
    unscheduledFilterContainer: {
      padding: theme.spacing(3),
      marginBottom: theme.spacing(2),
      backgroundColor: theme.paintscout.palette.brand.Neutral.P96
    },
    unscheduledFilterInputContainer: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: theme.spacing(3),
      [theme.breakpoints.up('md')]: {
        gap: theme.spacing(4)
      }
    },
    unscheduledFilterTopDiv: {
      display: 'grid',
      gap: theme.spacing(2),
      [theme.breakpoints.up('md')]: {
        gridTemplateColumns: '1fr 1fr'
      }
    },
    unscheduledFilterInnerButtonContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      marginTop: theme.spacing(3),
      paddingTop: theme.spacing(3),
      borderTop: `1px solid #d9dee8`
    },
    noResultsMessage: {
      // "py-8 text-center"
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: theme.spacing(8),
      paddingBottom: theme.spacing(8),
      textAlign: 'center'
    },

    noResultsMessageButton: {
      marginTop: theme.spacing()
    },
    noResultsMessageButtonText: {
      color: theme.paintscout.palette.brand.Blue.Blue65
    },
    jobCardContainer: {
      // Mobile (xs): horizontal scroll. Tablet (sm/md): 2 columns. Desktop (lg+): 4 columns.
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        display: 'flex',
        flexDirection: 'row',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollSnapType: 'x mandatory',
        // Bleed past the container's horizontal padding on the right so cards can
        // peek off-screen, while keeping a small inset on the left so the first
        // card has breathing room from the section's edge.
        marginLeft: 0,
        marginRight: theme.spacing(-1.5),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1.5),
        paddingBottom: theme.spacing(0.5),
        '& > *': {
          flexShrink: 0,
          width: '80%',
          minWidth: 260,
          maxWidth: 400,
          scrollSnapAlign: 'start'
        }
      },
      [theme.breakpoints.up('sm')]: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
      },
      [theme.breakpoints.up('lg')]: {
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))'
      }
    },
    hasMoreContainer: {
      // "mt-4 flex justify-center"
      marginTop: theme.spacing(2),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    showMoreIcon: {
      width: theme.spacing(3),
      height: theme.spacing(3),
      marginRight: theme.spacing(1)
    },
    loadMoreTailCard: {
      // Doubled selector to override `.jobCardContainer > *` width/minWidth on mobile.
      '&&': {
        flexShrink: 0,
        width: 'auto',
        minWidth: 96,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(0, 2)
      }
    }
  }),
  { name: 'UnscheduledJobs' }
)

function CustomDragLayer() {
  const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    currentOffset: monitor.getClientOffset()
  }))
  const { options } = useClientOptions()
  const crews = useMemo(() => getCrews({ options }), [options])

  if (!isDragging || !currentOffset || !item?.event || !item.isUnscheduled) {
    return null
  }

  const crewColor = crews.find((c) => c._id === item.event.crew)?.color ?? '#94a3b8'

  // Snap x to the left edge of the hovered date cell, shifted one cell left
  // via previousElementSibling to match where the block will actually land.
  // Falls back to cursor position when not over the schedule grid.
  let snappedX = currentOffset.x
  for (const el of document.elementsFromPoint(currentOffset.x, currentOffset.y)) {
    const dayCell = el.closest('[data-date]')
    if (dayCell) {
      const prevCell = dayCell.previousElementSibling
      const targetCell = prevCell?.hasAttribute('data-date') ? prevCell : dayCell
      snappedX = (targetCell as HTMLElement).getBoundingClientRect().left
      break
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: 100,
        left: snappedX,
        top: currentOffset.y,
        transform: 'translateY(-50%)'
      }}
    >
      <div
        style={{
          height: '40px',
          borderRadius: 4,
          padding: '0 16px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          fontSize: '0.75rem',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
          border: `1px solid ${crewColor}`,
          backgroundColor: `${crewColor}40`,
          width: 200
        }}
      >
        <Typography variant={'h6'} color={'textPrimary'} weight={'bold'} noWrap>
          {item.event.deal?.name}
        </Typography>
        <Typography variant={'subtitle1'} color={'textSecondary'} noWrap>
          {getProjectTypeLabel(item.event.projectType, options)}
        </Typography>
      </div>
    </div>
  )
}

export interface UnscheduledJobsProps {
  classes?: StyleClasses<typeof useStyles>
}

function getRowsForPage(args: { rows: any[]; page: number; rowsPerPage: number }) {
  const { rows, page, rowsPerPage } = args
  return rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
}

function UnscheduledJobs() {
  const classes = useStyles()
  const { options } = useClientOptions()
  const { users, userPictures } = useUsers()
  const {
    updateEvent,
    unscheduledEvents,
    totalUnscheduledEvents,
    fetchMoreUnscheduledEvents,
    loading,
    unscheduledFilters
  } = useProductionCalendar()
  const { canDeactivateSettingsItems } = useFlags()
  const crews = getCrews({ options })
  const { quote: quoteLabel, currency } = getObjectLabels({ options })
  const typeOptions = getQuoteTypeOptions({ options, hideAll: false, canDeactivate: !!canDeactivateSettingsItems }).map(
    (type) => ({
      value: type.key,
      label: type.label
    })
  )
  const productionPipeline = getPipelines({ options })?.find((pipeline) => pipeline.key === 'production-pipeline')
  const stageOptions = (productionPipeline?.stages || []).map((type) => ({
    value: type.key,
    label: type.label
  }))

  const tagOptions = getQuoteTags({ options })

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  const currentPage = useRef(1)
  const [pageChange, setPageChange] = useState(true)
  const [showingMore, setShowingMore] = useState(currentPage.current > 1 || unscheduledEvents.length > 8)
  const [isCollapsed, setIsCollapsed] = useLocalStorage<boolean>('productionCalendarUnscheduledCollapsed', false)
  const [showFilters, setShowFilters] = useState(false)

  const rowsPerPage = showingMore ? 8 : 4
  const hasMore = unscheduledEvents.length > 4
  const pages = Math.ceil(totalUnscheduledEvents / rowsPerPage)
  const [rowsForPage, setRowsForPage] = useState(
    getRowsForPage({ rows: unscheduledEvents, rowsPerPage, page: currentPage.current - 1 })
  )
  const isFirstPage = currentPage.current <= 1
  const isLastPage = currentPage.current >= pages || !pages || pages === 1

  // Mobile-only: horizontal infinite scroll on the unscheduled-jobs row.
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const isFetchingMoreRef = useRef(false)
  const hasMoreToLoad = unscheduledEvents.length < totalUnscheduledEvents

  useEffect(() => {
    if (!isMobile) return
    const el = scrollContainerRef.current
    if (!el) return

    const maybeLoadMore = () => {
      if (isFetchingMoreRef.current) return
      if (!hasMoreToLoad) return
      if (loading) return
      if (!fetchMoreUnscheduledEvents) return

      // Trigger ~one card-width before the right edge so cards stream in.
      const PREFETCH_THRESHOLD = 120
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - PREFETCH_THRESHOLD
      if (!atEnd) return

      isFetchingMoreRef.current = true
      setIsFetchingMore(true)
      ;(async () => {
        try {
          await fetchMoreUnscheduledEvents()
        } catch {
          // Swallow; the parent query's error handling owns user-facing errors.
        } finally {
          // Hold the guard through the next animation frame so React has time
          // to commit the new cards before another scroll event (or the
          // effect's own re-attach below) can trigger a second fetch. Without
          // this, a momentum-scroll that carries the user past the freshly
          // appended cards races the post-fetch re-render and fires a second
          // fetchMore for the page we just loaded.
          requestAnimationFrame(() => {
            isFetchingMoreRef.current = false
            setIsFetchingMore(false)
          })
        }
      })()
    }

    el.addEventListener('scroll', maybeLoadMore, { passive: true })
    // Re-check after layout settles, in case the new content already leaves us at the end
    // (e.g. fewer cards than viewport width).
    maybeLoadMore()

    return () => {
      el.removeEventListener('scroll', maybeLoadMore)
    }
  }, [isMobile, hasMoreToLoad, loading, fetchMoreUnscheduledEvents, unscheduledEvents.length])

  const [localMinPrice, setLocalMinPrice] = useState(unscheduledFilters.unscheduledMinPrice)
  const [localMaxPrice, setLocalMaxPrice] = useState(unscheduledFilters.unscheduledMaxPrice)
  const [localMinHours, setLocalMinHours] = useState(unscheduledFilters.unscheduledMinHours)
  const [localMaxHours, setLocalMaxHours] = useState(unscheduledFilters.unscheduledMaxHours)
  const [localSelectedProjectTypes, setLocalSelectedProjectTypes] = useState(
    unscheduledFilters.unscheduledSelectedProjectTypes
  )
  const [localSelectedProjectManagers, setLocalSelectedProjectManagers] = useState<string[]>(
    unscheduledFilters.unscheduledSelectedProjectManagers
  )
  const [localSelectedStages, setLocalSelectedStages] = useState(unscheduledFilters.unscheduledSelectedStages)
  const [localSelectedTags, setLocalSelectedTags] = useState(unscheduledFilters.unscheduledTags)

  // Update filtered events whenever filters or unscheduledEvents change
  React.useEffect(() => {
    setRowsForPage(getRowsForPage({ rows: unscheduledEvents, rowsPerPage, page: currentPage.current - 1 }))
  }, [
    unscheduledFilters.unscheduledSelectedProjectTypes,
    unscheduledFilters.unscheduledMinPrice,
    unscheduledFilters.unscheduledMaxPrice,
    unscheduledFilters.unscheduledMinHours,
    unscheduledFilters.unscheduledMaxHours,
    unscheduledFilters.unscheduledSelectedProjectManagers,
    unscheduledFilters.unscheduledSelectedStages,
    unscheduledFilters.unscheduledTags,
    unscheduledEvents.length,
    pageChange,
    showingMore
  ])

  const hasActiveFilters =
    unscheduledFilters.unscheduledSelectedProjectManagers.length > 0 ||
    unscheduledFilters.unscheduledSelectedProjectTypes.length > 0 ||
    unscheduledFilters.unscheduledMinPrice ||
    unscheduledFilters.unscheduledMaxPrice ||
    unscheduledFilters.unscheduledMinHours ||
    unscheduledFilters.unscheduledMaxHours ||
    unscheduledFilters.unscheduledSelectedStages.length > 0 ||
    unscheduledFilters.unscheduledTags.length > 0
  const clearFilters = () => {
    setShowFilters(false)
    unscheduledFilters.setUnscheduledSelectedProjectManagers([])
    unscheduledFilters.setUnscheduledSelectedProjectTypes([])
    unscheduledFilters.setUnscheduledMinPrice('')
    unscheduledFilters.setUnscheduledMaxPrice('')
    unscheduledFilters.setUnscheduledMinHours('')
    unscheduledFilters.setUnscheduledMaxHours('')
    unscheduledFilters.setUnscheduledSelectedStages([])
    unscheduledFilters.setUnscheduledTags([])
    setLocalSelectedProjectManagers([])
    setLocalSelectedStages([])
    setLocalSelectedTags([])
    setLocalSelectedProjectTypes([])
    setLocalMinPrice(null)
    setLocalMaxPrice(null)
    setLocalMinHours(null)
    setLocalMaxHours(null)
  }

  const removeUnscheduledProjectManager = (id: string) => {
    const next = unscheduledFilters.unscheduledSelectedProjectManagers.filter((x) => x !== id)
    unscheduledFilters.setUnscheduledSelectedProjectManagers(next)
    setLocalSelectedProjectManagers(next)
  }
  const removeUnscheduledProjectType = (value: string) => {
    const next = unscheduledFilters.unscheduledSelectedProjectTypes.filter((x) => x !== value)
    unscheduledFilters.setUnscheduledSelectedProjectTypes(next)
    setLocalSelectedProjectTypes(next)
  }
  const removeUnscheduledStage = (value: string) => {
    const next = unscheduledFilters.unscheduledSelectedStages.filter((x) => x !== value)
    unscheduledFilters.setUnscheduledSelectedStages(next)
    setLocalSelectedStages(next)
  }
  const removeUnscheduledTag = (value: string) => {
    const next = unscheduledFilters.unscheduledTags.filter((x) => x !== value)
    unscheduledFilters.setUnscheduledTags(next)
    setLocalSelectedTags(next)
  }
  const clearUnscheduledPriceFilters = () => {
    unscheduledFilters.setUnscheduledMinPrice('')
    unscheduledFilters.setUnscheduledMaxPrice('')
    setLocalMinPrice(null)
    setLocalMaxPrice(null)
  }
  const clearUnscheduledHoursFilters = () => {
    unscheduledFilters.setUnscheduledMinHours('')
    unscheduledFilters.setUnscheduledMaxHours('')
    setLocalMinHours(null)
    setLocalMaxHours(null)
  }

  const handleSchedule = (eventId: string, startDate: number, endDate: number, crewId: string, memberIds: string[]) => {
    const newCrew = crews.find((c) => c._id === crewId) || null
    const event = unscheduledEvents.find((e) => e.id === eventId)
    if (!event || !newCrew) return

    const allOwners = crews.flatMap((crew) =>
      [crew.projectManager, crew.siteLead, ...(crew.members ?? [])].filter((m): m is NonNullable<typeof m> => !!m?.id)
    )
    const assignedUsers =
      memberIds.length > 0
        ? Object.fromEntries(
            memberIds.map((id) => {
              const owner = allOwners.find((m) => m.id === id)
              const name = owner
                ? [owner.firstName, owner.lastName].filter(Boolean).join(' ').trim() || undefined
                : undefined
              return [removeAuth0Prefix(id), { assigned: true as const, ...(name ? { name } : {}) }]
            })
          )
        : undefined

    const updatedEvent: CalendarEvent = {
      ...event,
      crew: newCrew._id,
      start: new Date(startDate),
      end: new Date(endDate),
      ...(assignedUsers ? { assignedUsers } : {})
    }

    updateEvent(updatedEvent)
  }

  function handleChangePage(event, nextPage) {
    if (nextPage === 0 || nextPage > pages) return
    const nextPageFirstIndex = (nextPage - 1) * rowsPerPage
    const dataExists = !!unscheduledEvents[nextPageFirstIndex]

    if (!dataExists && fetchMoreUnscheduledEvents) {
      fetchMoreUnscheduledEvents()
    }

    currentPage.current = nextPage
    setPageChange(!pageChange)
  }

  function handleSearch() {
    // Apply local filter values to the global unscheduledFilters
    // only on the values that have changed to avoid unnecessary re-filtering

    if (localMinPrice !== unscheduledFilters.unscheduledMinPrice) {
      unscheduledFilters.setUnscheduledMinPrice(String(localMinPrice))
    }
    if (localMaxPrice !== unscheduledFilters.unscheduledMaxPrice) {
      unscheduledFilters.setUnscheduledMaxPrice(String(localMaxPrice))
    }
    if (localMinHours !== unscheduledFilters.unscheduledMinHours) {
      unscheduledFilters.setUnscheduledMinHours(String(localMinHours))
    }
    if (localMaxHours !== unscheduledFilters.unscheduledMaxHours) {
      unscheduledFilters.setUnscheduledMaxHours(String(localMaxHours))
    }
    if (localSelectedProjectTypes !== unscheduledFilters.unscheduledSelectedProjectTypes) {
      unscheduledFilters.setUnscheduledSelectedProjectTypes(localSelectedProjectTypes)
    }
    if (localSelectedProjectManagers !== unscheduledFilters.unscheduledSelectedProjectManagers) {
      unscheduledFilters.setUnscheduledSelectedProjectManagers(localSelectedProjectManagers)
    }
    if (localSelectedStages !== unscheduledFilters.unscheduledSelectedStages) {
      unscheduledFilters.setUnscheduledSelectedStages(localSelectedStages)
    }
    if (localSelectedTags !== unscheduledFilters.unscheduledTags) {
      unscheduledFilters.setUnscheduledTags(localSelectedTags)
    }

    setShowFilters(false)
  }

  if (unscheduledEvents.length === 0 && !unscheduledFilters) {
    return null
  }

  return (
    <div className={classes.unscheduledJobsContainer} style={isCollapsed ? { paddingBottom: 8 } : undefined}>
      <CustomDragLayer />
      <div
        className={classes.unscheduledJobsContainerHeader}
        style={isCollapsed ? { paddingBottom: 0, marginBottom: 0, borderBottom: 'none' } : undefined}
        onClick={() => {
          setIsCollapsed((prev) => !prev)
          setShowingMore(false)
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setIsCollapsed((prev) => !prev)
            setShowingMore(false)
          }
        }}
      >
        <div className={classes.unscheduledJobsContainerInner}>
          <div className={classes.unscheduledJobsTextContainer}>
            <span className={classes.countChip}>{totalUnscheduledEvents}</span>
            <Typography variant="h3" className={classes.unscheduledTitle}>
              Unscheduled
            </Typography>
            <Tooltip
              interactive
              title=""
              content={'All deals without a job that are in the production pipeline.'}
              icon={<InfoOutlinedIcon className={classes.infoIcon} />}
            />
          </div>
        </div>

        <div className={classes.unscheduledFilterButtonContainer}>
          <IconButton className={classes.headerIconButton} title={isCollapsed ? 'Expand' : 'Collapse'} tabIndex={-1}>
            {isCollapsed ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
          </IconButton>
          <div className={classes.headerDivider} aria-hidden />
          {hasActiveFilters && (
            <>
              <Hidden xsDown>
                <Button
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation()
                    clearFilters()
                  }}
                  title="Clear all filters"
                  size="small"
                  className={classes.headerClearButtonOutlined}
                >
                  <Typography variant="subtitle1">Clear</Typography>
                </Button>
              </Hidden>
              <Hidden smUp>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation()
                    clearFilters()
                  }}
                  className={classes.headerIconButton}
                  title="Clear all filters"
                >
                  <CloseIcon style={{ width: 16, height: 16 }} />
                </IconButton>
              </Hidden>
            </>
          )}
          <IconButton
            onClick={(e) => {
              e.stopPropagation()
              if (isCollapsed) setIsCollapsed(false)
              setShowFilters(!showFilters)
            }}
            className={classes.headerIconButton}
            title="Filter"
          >
            <FilterListIcon />
          </IconButton>
        </div>
      </div>

      {!isCollapsed && (
        <>
          {hasActiveFilters && (
            <div className={classes.unscheduledFilterChipsRow}>
              {unscheduledFilters.unscheduledSelectedProjectManagers.map((id) => {
                const user = users?.find((u) => removeAuth0Prefix(u.user_id) === removeAuth0Prefix(id))
                return (
                  <span key={`pm-${id}`} className={classes.unscheduledFilterChip}>
                    <button
                      type="button"
                      className={classes.unscheduledFilterChipRemove}
                      onClick={() => removeUnscheduledProjectManager(id)}
                      aria-label="Remove filter"
                    >
                      <CloseIcon className={classes.unscheduledFilterChipRemoveIcon} />
                    </button>
                    <EstimatorAvatar
                      user={{
                        id,
                        firstName: user?.user_metadata?.firstName,
                        lastName: user?.user_metadata?.lastName
                      }}
                      userPictures={userPictures}
                      width="20px"
                      height="20px"
                      fontSize="9px"
                      borderColor={user?.user_metadata?.color}
                      hideTooltip
                    />
                  </span>
                )
              })}
              {unscheduledFilters.unscheduledSelectedProjectTypes.map((value) => {
                const label = typeOptions.find((opt) => opt.value === value)?.label || value
                return (
                  <span key={`pt-${value}`} className={classes.unscheduledFilterChip}>
                    <button
                      type="button"
                      className={classes.unscheduledFilterChipRemove}
                      onClick={() => removeUnscheduledProjectType(value)}
                      aria-label="Remove filter"
                    >
                      <CloseIcon className={classes.unscheduledFilterChipRemoveIcon} />
                    </button>
                    {label}
                  </span>
                )
              })}
              {unscheduledFilters.unscheduledSelectedStages.map((value) => {
                const label = stageOptions.find((opt) => opt.value === value)?.label || value
                return (
                  <span key={`stg-${value}`} className={classes.unscheduledFilterChip}>
                    <button
                      type="button"
                      className={classes.unscheduledFilterChipRemove}
                      onClick={() => removeUnscheduledStage(value)}
                      aria-label="Remove filter"
                    >
                      <CloseIcon className={classes.unscheduledFilterChipRemoveIcon} />
                    </button>
                    {label}
                  </span>
                )
              })}
              {unscheduledFilters.unscheduledTags.map((tagName) => {
                const label = tagOptions.find((opt) => opt.name === tagName)?.label || tagName
                return (
                  <span key={`tag-${tagName}`} className={classes.unscheduledFilterChip}>
                    <button
                      type="button"
                      className={classes.unscheduledFilterChipRemove}
                      onClick={() => removeUnscheduledTag(tagName)}
                      aria-label="Remove filter"
                    >
                      <CloseIcon className={classes.unscheduledFilterChipRemoveIcon} />
                    </button>
                    {label}
                  </span>
                )
              })}
              {(unscheduledFilters.unscheduledMinPrice || unscheduledFilters.unscheduledMaxPrice) && (
                <span className={classes.unscheduledFilterChip}>
                  <button
                    type="button"
                    className={classes.unscheduledFilterChipRemove}
                    onClick={clearUnscheduledPriceFilters}
                    aria-label="Remove filter"
                  >
                    <CloseIcon className={classes.unscheduledFilterChipRemoveIcon} />
                  </button>
                  {`${currency.symbol}${unscheduledFilters.unscheduledMinPrice || '0'} - ${currency.symbol}${
                    unscheduledFilters.unscheduledMaxPrice || '∞'
                  }`}
                </span>
              )}
              {(unscheduledFilters.unscheduledMinHours || unscheduledFilters.unscheduledMaxHours) && (
                <span className={classes.unscheduledFilterChip}>
                  <button
                    type="button"
                    className={classes.unscheduledFilterChipRemove}
                    onClick={clearUnscheduledHoursFilters}
                    aria-label="Remove filter"
                  >
                    <CloseIcon className={classes.unscheduledFilterChipRemoveIcon} />
                  </button>
                  {`${unscheduledFilters.unscheduledMinHours || '0'} - ${unscheduledFilters.unscheduledMaxHours || '∞'} hrs`}
                </span>
              )}
            </div>
          )}

          {showFilters && (
            <div className={classes.unscheduledFilterContainer}>
              <div className={classes.unscheduledFilterInputContainer}>
                {/* Price Range and Hours Range - Side by Side */}
                <Grid container alignItems="flex-end" spacing={2}>
                  {/* Price Range */}
                  <Grid item sm={3} xs={6}>
                    <KeypadInputField
                      label={`Amount (${currency.symbol})`}
                      margin="none"
                      placeholder="Min"
                      format="dimension"
                      allowZero
                      fullWidth
                      allowClear
                      value={localMinPrice}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const { value } = event.target
                        setLocalMinPrice(value === '' ? null : Number(value))
                      }}
                    />
                  </Grid>
                  <Grid item sm={3} xs={6}>
                    <KeypadInputField
                      margin="none"
                      flipX
                      placeholder="Max"
                      format="dimension"
                      allowZero
                      value={localMaxPrice}
                      fullWidth
                      allowClear
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const { value } = event.target
                        setLocalMaxPrice(value === '' ? null : Number(value))
                      }}
                    />
                  </Grid>
                  {/* Hours Range */}
                  <Grid item sm={3} xs={6}>
                    <KeypadInputField
                      label={`Hours`}
                      margin="none"
                      placeholder="Min"
                      format="dimension"
                      allowZero
                      fullWidth
                      allowClear
                      value={localMinHours}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const { value } = event.target
                        setLocalMinHours(value === '' ? null : Number(value))
                      }}
                    />
                  </Grid>
                  <Grid item sm={3} xs={6}>
                    <KeypadInputField
                      margin="none"
                      flipX
                      placeholder="Max"
                      format="dimension"
                      allowZero
                      value={localMaxHours}
                      fullWidth
                      allowClear
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const { value } = event.target
                        setLocalMaxHours(value === '' ? null : Number(value))
                      }}
                    />
                  </Grid>
                </Grid>
                {/* Estimate Type and Estimator - Side by Side */}
                {/* Project Type Filter */}
                <Grid container alignItems="flex-end" spacing={2}>
                  <Grid item xs={12} lg={3}>
                    <DropdownSelect
                      label={`${quoteLabel.value} Type`}
                      variant="multi"
                      options={typeOptions}
                      hideCloseButton
                      placeholder={{
                        selectPlaceholder: `Select ${quoteLabel.value.toLowerCase()} type(s)`,
                        plural: `Types`
                      }}
                      value={localSelectedProjectTypes.map((type) => {
                        return {
                          value: type,
                          label: typeOptions.find((opt) => opt.value === type)?.label || ''
                        }
                      })}
                      onChange={(selectedOptions) => {
                        setLocalSelectedProjectTypes(selectedOptions.map((option) => option.value))
                      }}
                    />
                  </Grid>

                  {/* Sales Rep Filter */}
                  <Grid item xs={12} lg={3}>
                    <EstimatorSelect
                      name="estimator"
                      label="Estimator"
                      variant="multi"
                      value={localSelectedProjectManagers?.map((val) => addAuth0Prefix(val as string))}
                      roles={['sales', 'admin']}
                      placeholder={{
                        selectPlaceholder: 'Select estimator(s)',
                        plural: 'Estimators'
                      }}
                      onChange={(options: DropdownSelectOption[]) => {
                        setLocalSelectedProjectManagers(options.map((option) => removeAuth0Prefix(option.value)))
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} lg={3}>
                    <DropdownSelect
                      label="Stage"
                      variant="multi"
                      options={stageOptions}
                      hideCloseButton
                      onChange={(options: DropdownSelectOption[]) => {
                        setLocalSelectedStages(options.map((option) => option.value))
                      }}
                      value={localSelectedStages.map((stage) => {
                        return {
                          value: stage,
                          label: stageOptions.find((opt) => opt.value === stage)?.label || ''
                        }
                      })}
                      placeholder={{
                        selectPlaceholder: `Select stage(s)`,
                        plural: `Stages`
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} lg={3}>
                    <TagSelection
                      label="Tags"
                      appliedTags={localSelectedTags.map((tag) => tagOptions.find((opt) => opt.name === tag))}
                      handleChange={(tags: QuoteTag[]) => {
                        setLocalSelectedTags(tags.map((tag) => tag.name))
                      }}
                      placeholder={{
                        selectPlaceholder: `Select tag(s)`,
                        plural: `Stages`
                      }}
                    />
                  </Grid>
                </Grid>
              </div>

              {/* Action Buttons */}
              <div className={classes.unscheduledFilterInnerButtonContainer}>
                <Button variant="text" onClick={clearFilters}>
                  Clear
                </Button>
                <Button variant="contained" onClick={() => handleSearch()}>
                  Search
                </Button>
              </div>
            </div>
          )}

          {unscheduledEvents.length === 0 && hasActiveFilters ? (
            <div className={classes.noResultsMessage}>
              <Typography variant={'body1'} color="textSecondary">
                No jobs match the selected filters
              </Typography>
              <Button
                variant={'text'}
                fullWidth={false}
                onClick={clearFilters}
                className={classes.noResultsMessageButton}
              >
                <Typography variant="subtitle1" className={classes.noResultsMessageButtonText}>
                  Clear all filters
                </Typography>
              </Button>
            </div>
          ) : (
            <>
              <div className={classes.jobCardContainer} ref={scrollContainerRef}>
                {loading && <Spinner />}

                {!loading &&
                  (isMobile ? unscheduledEvents : rowsForPage).map((event) => (
                    <UnscheduledJobCard key={event.id} event={event} onSchedule={handleSchedule} />
                  ))}

                {!loading && isMobile && isFetchingMore && (
                  <div className={classes.loadMoreTailCard}>
                    <Spinner />
                  </div>
                )}
              </div>

              <Hidden xsDown>
                {hasMore && (
                  <div className={classes.hasMoreContainer}>
                    {showingMore ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Button
                          variant="text"
                          disabled={loading || isFirstPage}
                          onClick={() => handleChangePage(null, currentPage.current - 1)}
                        >
                          Prev
                        </Button>
                        <Typography variant="body2" style={{ margin: '0 8px' }}>
                          Page {currentPage.current} of {pages}
                        </Typography>
                        <Button
                          variant="text"
                          disabled={loading || isLastPage}
                          onClick={() => handleChangePage(null, currentPage.current + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant={'text'}
                        onClick={() => {
                          setShowingMore((showingMore) => !showingMore)
                        }}
                      >
                        {showingMore ? (
                          <>
                            <KeyboardArrowUpIcon className={classes.showMoreIcon} />
                            <Typography variant="body2">Show Less</Typography>
                          </>
                        ) : (
                          <>
                            <KeyboardArrowDownIcon className={classes.showMoreIcon} />
                            <Typography variant="body2">
                              View {unscheduledEvents.length - 4} More{' '}
                              {unscheduledEvents.length - 4 === 1 ? 'Job' : 'Jobs'}
                            </Typography>
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </Hidden>
            </>
          )}
        </>
      )}
    </div>
  )
}

export default UnscheduledJobs
